// Mostrador de uma grandeza, com a faixa em pu do nominal do regulador e um traço no
// nominal. O número fica na cor do texto; só o arco leva a cor de destaque (a cor 1 da
// paleta validada). Regulador sem link: tudo em cinza, para não parecer valor vivo.

import { useMemo } from "react";
import type { GaugeMode } from "../../lib/gauges";
import { seriesColor, token } from "../../lib/palette";
import { type Display, formatValue } from "../../lib/units";
import { cx } from "../ui";
import { EChart, useThemeVersion } from "./EChart";

export interface GaugeProps {
  title: string;
  /** Valor em SI; nulo quando a grandeza ainda não chegou. */
  value: number | null;
  /** Nominal em SI. */
  nominal: number;
  range: [number, number];
  mode: GaugeMode;
  display: Display;
  stale?: boolean;
  height?: number;
}

const compact = (v: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: Math.abs(v) >= 10 ? 0 : 1 });

export function Gauge({ title, value, nominal, range, mode, display, stale, height = 150 }: GaugeProps) {
  const theme = useThemeVersion();
  const min = (range[0] * nominal) / display.factor;
  const max = (range[1] * nominal) / display.factor;
  const nom = nominal / display.factor;
  const shown = value === null ? null : value / display.factor;
  // Desvio (frequência): a diferença para o nominal diz mais que "100 % do nominal".
  const pct = value === null ? null : Math.round((value / nominal) * 100);
  const deviation = value === null ? null : ((value - nominal) / nominal) * 100;
  const relative =
    mode === "deviation" && range[0] > 0
      ? deviation === null
        ? null
        : `${deviation >= 0 ? "+" : ""}${deviation.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}% do nominal`
      : pct === null
        ? null
        : `${pct}% do nominal`;
  const text = formatValue(value, display);

  const option = useMemo(() => {
    const accent = stale ? token("--muted") : seriesColor(0);
    const track = token("--surface-2");
    const tick = token("--muted");
    const f = (nom - min) / (max - min);
    return {
      animationDurationUpdate: 700,
      series: [
        {
          type: "gauge",
          startAngle: 215,
          endAngle: -35,
          min,
          max,
          splitNumber: 2,
          radius: "96%",
          center: ["50%", "60%"],
          axisLine: {
            lineStyle: {
              width: 10,
              // Trilho neutro com um traço no nominal.
              color: [
                [Math.max(0, f - 0.006), track],
                [Math.min(1, f + 0.006), tick],
                [1, track],
              ],
            },
          },
          progress: { show: mode === "magnitude", width: 10, itemStyle: { color: accent } },
          pointer:
            mode === "deviation"
              ? { show: true, length: "62%", width: 4, offsetCenter: [0, 0], itemStyle: { color: accent } }
              : { show: false },
          anchor: mode === "deviation" ? { show: true, size: 9, itemStyle: { color: accent } } : { show: false },
          axisTick: { show: false },
          splitLine: { distance: -10, length: 10, lineStyle: { width: 1, color: token("--surface") } },
          axisLabel: { distance: 14, color: token("--muted"), fontSize: 10, formatter: compact },
          title: { show: false },
          // No de desvio o ponteiro ocupa o centro: o número vai para baixo do mostrador (HTML).
          detail: {
            show: mode === "magnitude",
            valueAnimation: false,
            offsetCenter: [0, "8%"],
            fontSize: 20,
            fontWeight: 600,
            color: stale ? token("--muted") : token("--fg"),
            formatter: () => text,
          },
          data: [{ value: shown === null ? min : Math.min(max, Math.max(min, shown)) }],
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min, max, nom, shown, text, mode, stale, theme]);

  const label = `${title}: ${text} ${display.unit}${relative === null ? "" : `, ${relative}`}`;
  return (
    <div className={cx("min-w-0 text-center", stale && "opacity-80")}>
      <p className="truncate text-xs font-medium text-muted" title={title}>
        {title}
      </p>
      <EChart option={option} height={height} label={label} />
      {mode === "deviation" && <p className={cx("-mt-5 text-xl font-semibold", stale ? "text-muted" : "text-fg")}>{text}</p>}
      <p className={cx("text-xs text-muted", mode === "magnitude" && "-mt-3")}>
        {display.unit}
        {relative !== null && <span className="tabular-nums"> · {relative}</span>}
      </p>
    </div>
  );
}
